import { z } from "zod";
import { examCodeSchema, programmeCodeSchema } from "./programmes";

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Use a date like 2027-05-14");
const time = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Use a time like 09:00");

export const notificationPrefsSchema = z.object({
  daily_reminder: z.boolean(),
  set_ready: z.boolean(),
  weekly_report: z.boolean(),
});

/** What GET /profile returns (spec §5.2 profiles + §8.3 deletion state). */
export const profileSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email().nullable(),
  display_name: z.string(),
  programme: programmeCodeSchema.nullable(),
  exam: examCodeSchema.nullable(),
  exam_date: isoDate.nullable(),
  school: z.string().nullable(),
  daily_goal_minutes: z.number().int(),
  timezone: z.string(),
  reminder_time: time,
  notification_prefs: notificationPrefsSchema,
  onboarding_completed: z.boolean(),
  deletion_scheduled_for: z.string().nullable(),
  xp_total: z.number().int(),
  level: z.number().int(),
});
export type Profile = z.infer<typeof profileSchema>;

export const DAILY_GOAL_OPTIONS = [10, 20, 30, 45, 60] as const;

/**
 * POST /profile/onboarding (spec §8.2): programme, exam and daily goal are required to finish. The exam date is
 * optional (plan BE8) — many students haven't booked yet; without it the config in force today is used.
 */
export const onboardingRequestSchema = z.object({
  display_name: z.string().trim().max(80).optional(),
  programme: programmeCodeSchema,
  exam: examCodeSchema,
  exam_date: isoDate.nullable().default(null),
  /** Onboarding step 3 (OD-1): start with the StudyWard Core library, or only the student's own material. */
  core_library: z.boolean().optional(),
  daily_goal_minutes: z.number().int().min(5).max(240),
  timezone: z.string().min(1).max(64),
});
export type OnboardingRequest = z.infer<typeof onboardingRequestSchema>;

/** PATCH /profile: editable later in Settings (§8.2). Programme changes affect future sets only. */
export const profilePatchSchema = z
  .object({
    display_name: z.string().trim().max(80),
    programme: programmeCodeSchema,
    exam: examCodeSchema,
    /** null = "not booked yet". */
    exam_date: isoDate.nullable(),
    school: z.string().trim().max(120).nullable(),
    daily_goal_minutes: z.number().int().min(5).max(240),
    timezone: z.string().min(1).max(64),
    reminder_time: time,
    notification_prefs: notificationPrefsSchema,
  })
  .partial()
  .refine((p) => (p.programme === undefined) === (p.exam === undefined), "Change programme and exam together")
  .refine((p) => Object.keys(p).length > 0, "Nothing to update");
export type ProfilePatch = z.infer<typeof profilePatchSchema>;

export const ACCOUNT_DELETION_GRACE_DAYS = 30;

export const deletionResponseSchema = z.object({ deletion_scheduled_for: z.string().nullable() });
